import { useEffect, useState } from 'react';
import { components } from 'react-select';
import {
  Button,
  CheckboxContainer,
  ReactSelectLabel,
} from './TreeSelectStyles';
import classNames from 'classnames';
import { Select } from 'components/Select';
import { TagDefinition } from 'types/TagRefDefinition';

export const flatten = (array: any) => {
  let result: any = [];
  array.forEach((elem: any) => {
    result.push(elem);
    if (Array.isArray(elem.childTags)) {
      result = result.concat(flatten(elem.childTags));
    }
  });
  return result;
};

const flattenIds = (array: any) => {
  let result: any = [];
  array.forEach((elem: any) => {
    result.push(elem.id);
    if (Array.isArray(elem.childTags)) {
      result = result.concat(flattenIds(elem.childTags));
    }
  });
  return result;
};

interface TreeSelectorProps {
  options: Array<any>;
  value: any;
  onChange: (event: any) => void;
  errorMessage?: string;
  isMulti?: boolean;
  testid?: string;
  hasError?: boolean;
}

export const TreeSelect = ({
  options,
  value,
  onChange,
  errorMessage,
  hasError,
  isMulti = false,
  testid,
}: TreeSelectorProps) => {
  const [selections, setSelections] = useState<Array<string>>([]);
  const [flatOptions, setFlatOptions] = useState([]);
  const [hash, setHash] = useState<any>({});

  useEffect(() => {
    if (value) {
      if (typeof value === 'string') {
        setSelections(value?.split(',') ?? []);
      } else {
        setSelections([value + '']);
      }
    } else {
      setSelections([]);
    }
  }, [value]);

  useEffect(() => {
    setFlatOptions(flatten(options));
  }, [options]);

  useEffect(() => {
    if (flatOptions) {
      const map: any = {};
      flatOptions?.forEach((opt: any) => {
        map[opt.id + ''] = flattenIds(opt.childTags ?? []).concat(opt.path);
      });
      setHash(map);
    }
  }, [flatOptions]);

  function isNodeDisabled(nodeId: any) {
    const id = parseInt(nodeId);
    let disabled = false;
    selections?.forEach((sel) => {
      if (hash[sel]?.includes(id)) {
        disabled = true;
        return;
      }
    });
    return disabled;
  }

  const SingleValue = (props: any) => {
    const length = selections.length;
    if (length === 0) return null;
    let label = `${length} selected`;
    if (length === 1) {
      label = props.options.find((opt: any) => opt.id === +selections[0])?.name;
    }
    return <div style={{ position: 'absolute', left: 10 }}>{label}</div>;
  };

  const TreeDataOption = (props: any) => {
    const node = props?.data as any;
    const nodeId = node?.id + '';
    const selected = selections.includes(nodeId);
    const padding = node.path?.length * 1.5;
    const isDisabled = isMulti ? isNodeDisabled(nodeId) : false;

    return (
      <Button padding={padding} disabled={isDisabled} onClick={props.onChange}>
        <components.Option {...props}>
          <CheckboxContainer
            disabled={isDisabled}
            defaultChecked={selected}
            type='checkbox'
            className={classNames('regularCheckbox bigCheckbox', {
              intermediateCheckbox: isDisabled,
            })}
            onClick={props.onChange}
          />
          <ReactSelectLabel title={props.value}>{props.value}</ReactSelectLabel>
        </components.Option>
      </Button>
    );
  };

  const onChangeHandler = (
    isMulti: boolean,
    val: TagDefinition,
    onChange: (event: any) => void
  ) => {
    const nodeId = val?.id + '';

    let updatedSelections: Array<any> = [];
    if (!isMulti) {
      updatedSelections = [nodeId];
    } else {
      if (selections.includes(nodeId)) {
        updatedSelections = selections.filter((item) => item !== nodeId);
      } else if (!isNodeDisabled(nodeId)) {
        updatedSelections = [...selections, nodeId];
      } else {
        updatedSelections = [...selections];
      }
    }
    setSelections(updatedSelections);
    onChange(updatedSelections.join(','));
  };

  return (
    <Select
      testid={testid}
      isTree={true}
      placeholder='Search for component'
      options={flatOptions || []}
      getOptionLabel={(opt: any) => opt.name}
      getOptionValue={(opt: any) => opt.name}
      onChange={(val) => {
        onChangeHandler(isMulti, val, onChange);
      }}
      components={{
        Option: TreeDataOption,
        SingleValue,
      }}
      hasError={hasError}
      errorMessage={errorMessage}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      backspaceRemovesValue={false}
      value={selections}
      style={{ width: 250 }}
      styles={{
        control: (provided) => ({
          ...provided,
          width: 250,
        }),
        option: (provided) => ({
          ...provided,
          background: 'none',
        }),
        menu: (provided) => ({
          ...provided,
          width: 400,
        }),
      }}
    />
  );
};
